/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import "./Wizzard.css";
import { version } from "version";

import React from "react";

import { WizzardProps, WizzardState } from "./Wizzard.types";

import wizzardPages, { ActionWizzardTargetableAction } from "./WizzardPages";

export default class SideBar extends React.Component<WizzardProps, WizzardState> {

   private decsRef = React.createRef<HTMLDivElement>();
   private highRef = React.createRef<HTMLDivElement>();
   private highNavbarRef = React.createRef<HTMLDivElement>();

   private lastPage: number = -1;

   private pages = wizzardPages;

   constructor(props: WizzardProps) {

      super(props);

      this.state = {
         page: 0,
         actionsRunning: false
      }

   }

   public componentDidMount() {
      window.addEventListener("resize", this.onWindowResize);
      window.addEventListener("keydown", this.onWindowKeyDown);
   }

   public componentWillUnmount() {
      window.removeEventListener("resize", this.onWindowResize);
      window.removeEventListener("keydown", this.onWindowKeyDown);
   }


   public render(): React.ReactNode {

      let displayWizzard = true;
      const _dontDisplayWizzard = window.localStorage.getItem("dont_display_wizzard");
      if (_dontDisplayWizzard === null || _dontDisplayWizzard === "false") displayWizzard = false;

      if (this.lastPage !== this.state.page) {
         this.highlight(this.pages[this.state.page].highlight, this.pages[this.state.page].padding, this.pages[this.state.page].preventScroll ? false : true);
         this.lastPage = this.state.page;
         this.runActions();
      }

      return (

         <div className="wizzard">

            <div className="no-click"></div>

            <div className="description" ref={this.decsRef}>

               <div className="cross" onMouseDown={() => this.close()}></div>
               <div className="version">{version}</div>
               <h3>{this.pages[this.state.page].caption}</h3>

               <div className="content">{this.pages[this.state.page].content}</div>

               <div className="dontshow">
                  <input type="checkbox" id="dontshow" checked={displayWizzard} onChange={this.onDontShowChanged} />
                  <label htmlFor="dontshow">Don't show this guide on startup</label>
               </div>

               <div className="nav">

                  <button
                     disabled={this.state.page === 0 || this.state.actionsRunning}
                     onClick={() => { if (this.state.page > 0) this.setState({ page: this.state.page - 1 }) }}
                  >
                     Previous
                  </button>

                  &nbsp;&nbsp;&nbsp;

                  <button
                     disabled={this.state.actionsRunning}
                     onClick={() => { if (this.state.page < this.pages.length - 1) this.setState({ page: this.state.page + 1 }); else this.close(); }}
                  >
                     {this.state.page === this.pages.length - 1 ? "Finish" : "Next"}
                  </button>

               </div>

            </div>

            <div className="highlighter-navbar">
               <div className="highlight" ref={this.highNavbarRef}></div>
            </div>

            <div className="highlighter">
               <div className="highlight" ref={this.highRef}>
               </div>
            </div>

         </div>

      )

   }

   private highlight(selector: string, padding: string = "0px", scrollToView?: boolean) {

      if (!this.highRef.current || !this.highNavbarRef.current) return;

      this.highNavbarRef.current.style.display = "none";
      this.highRef.current.style.display = "none";

      if (selector === "") return;

      const el: HTMLElement | null = document.querySelector(selector);
      if (!el) return;

      const pos = el.getBoundingClientRect();

      if (selector.indexOf(".navbar") !== -1) {

         this.highNavbarRef.current.style.left = `calc(${pos.left}px + ${padding}`;
         this.highNavbarRef.current.style.top = `calc(${pos.top}px + ${padding}`;

         this.highNavbarRef.current.style.width = `calc(${el.offsetWidth}px - (2 * ${padding}))`;
         this.highNavbarRef.current.style.height = `calc(${el.offsetHeight}px - (2 * ${padding}))`;

         this.highNavbarRef.current.style.display = "block";

         return;
      }

      let scrollX = 0;
      let scrollY = 0;

      let pe = el.parentElement;
      while (pe) {
         scrollX += pe.scrollLeft;
         scrollY += pe.scrollTop;
         pe = pe.parentElement;
      }

      this.highRef.current.style.left = `calc(${pos.left + scrollX}px + ${padding}`;
      this.highRef.current.style.top = `calc(${pos.top + scrollY}px + ${padding}`;

      this.highRef.current.style.width = `calc(${el.offsetWidth}px - (2 * ${padding}))`;
      this.highRef.current.style.height = `calc(${el.offsetHeight}px - (2 * ${padding}))`;

      this.highRef.current.style.display = "block";

      if (scrollToView) setTimeout(() => { el.scrollIntoView({ block: "start", behavior: "smooth" }); }, 100);


   }

   private async runActions(): Promise<void> {

      if (this.pages[this.state.page].actions === undefined) return;

      this.setState({ actionsRunning: true });

      for (const action of this.pages[this.state.page].actions!) {

         let target;
         if ((action as ActionWizzardTargetableAction).target) target = document.querySelector((action as ActionWizzardTargetableAction).target) as HTMLElement;

         switch (action.type) {

            case "keypress":
               if (!target) continue;
               await this.keypress(target, action.data, action.speed)
               break;

            case "delay":
               await this.delay(action.time);
               break;

            case "dblclick":
               if (!target) continue;
               await this.dblclick(target);
               break;

            case "highlight":
               if (!target) continue;
               this.highlight(action.target, action.padding, action.scrollToView);
               break;

         }

      }

      this.setState({ actionsRunning: false });

   }

   private delay(time: number): Promise<void> {

      return new Promise(
         (resolve) => {
            setTimeout(
               () => { resolve(); },
               time
            )
         }
      )

   }

   private dblclick(target: HTMLElement) {

      let event = new Event('dblclick', {bubbles: true });
      target.dispatchEvent(event);

   }


   private keypress(target: HTMLElement, text: string, speed: number = 350): Promise<void> {

      function setTargetValue(value: string) {

         let lastValue = (target as HTMLInputElement).value;
         (target as HTMLInputElement).value = value;

         let event = new Event('input', { bubbles: true });
         // hack React15
         (event as any).simulated = true;

         // hack React16
         let tracker = (target as any)._valueTracker;
         if (tracker) {
           tracker.setValue(lastValue);
         }

         target.dispatchEvent(event);

      }

      return new Promise<void>(

         (resolve) => {

            target.focus();
            setTargetValue("");

            let i = 0;

            function typeChar() {

               setTargetValue((target as HTMLInputElement).value + text[i]);
               i++;

               if (i < text.length) {
                  setTimeout(typeChar, speed);
               } else {
                  target.blur();
                  resolve();
               }

            }

            setTimeout(typeChar, 0);

         }

      )

   }

   private onWindowResize = () => {
      this.highlight(this.pages[this.state.page].highlight);
   }

   private onWindowKeyDown = (e: KeyboardEvent) => {

      switch(e.key) {

         case "Tab":
            e.preventDefault();
            e.stopPropagation();
            break;


         case "Escape":
            this.close();
            e.preventDefault();
            e.stopPropagation();
            break;

      }

   }

   private close = () => {

      if (this.props.onClose) this.props.onClose();

   }


   private onDontShowChanged = (e: React.ChangeEvent<HTMLInputElement>) => {

      window.localStorage.setItem("dont_display_wizzard", e.target.checked ? "true" : "false");
      this.forceUpdate();

   }

}
